import { DataTable } from '../../glitch-ui/data-table';
import { ColumnDef } from '@tanstack/react-table';
import { useOrganizationInitialization } from '@/hooks/useOrganizationInitialization';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Check, Eye, Link2, CheckCircle, MoreHorizontal, Plus, ArrowLeftRight } from 'lucide-react';
import googleAds from '/google-ads.svg';
import { useNavigate } from '@tanstack/react-router';
import { GoogleAdAccountSelectionModal } from '@/components/glitch-ui/google-ad-account-selection-modal';
import { TransferCampaignsModal } from '@/components/campaign/transfer-campaigns-modal';
import { useCampaignTransferMutation } from '@/lib/query-options';
import { useToast } from '@/hooks/useToast';
import { useState } from 'react';
import type { OrganizationDataWithMembership } from '@/lib/types';

type OrganizationData = {
  id: string;
  name: string;
  plan_type: string;
  user_role: string;
  google_ads_account: string;
  created_at: string;
  isSelected: boolean;
  user_count: number;
  campaign_count: number;
};

export const OrganizationInfo = () => {
  const { organizations, organizationData, switchOrganization, isSwitching } = useOrganizationInitialization();
  const [isGoogleAdsModalOpen, setIsGoogleAdsModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [targetOrgForTransfer, setTargetOrgForTransfer] = useState<OrganizationDataWithMembership | null>(null);
  const transferMutation = useCampaignTransferMutation();
  const { toast } = useToast();
  const navigate = useNavigate();

  if (!organizations || organizations.length === 0) {
    return <div>Loading organizations...</div>;
  }

  const handleTransfer = async (campaignIds: number[], targetOrganizationId: number, sourceOrganizationId: number) => {
    if (!targetOrgForTransfer) return;
    
    try {
      await transferMutation.mutateAsync({
        campaignIds,
        targetOrganizationId,
        sourceOrganizationId,
      });
      
      toast({
        description: `${campaignIds.length} campaign(s) have been transferred successfully.`,
      });
      
      setIsTransferModalOpen(false);
      setTargetOrgForTransfer(null);
    } catch (error) {
      toast({
        description: 'Failed to transfer campaigns',
        variant: 'destructive',
      });
    }
  };

  const organizationsTableData: OrganizationData[] = organizations.map(org => ({
    id: org.id?.toString() || '',
    name: org.name || 'N/A',
    plan_type: org.plan_type || 'N/A',
    user_role: org.membership?.role || 'Member',
    google_ads_account: org.membership?.last_selected_googleads_account_name || 'None',
    created_at: org.created_at ? new Date(org.created_at).toLocaleDateString() : 'N/A',
    isSelected: org.id === organizationData?.id,
    user_count: org.user_count || 0,
    campaign_count: org.campaign_count || 0,
  }));

  const handleRowClick = (row: OrganizationData) => {
    // Don't navigate if switching is in progress
    if (isSwitching) return;
    
    const org = organizations.find(o => o.id?.toString() === row.id);
    if (org) {
      if (!row.isSelected) {
        // Switch organization first, then navigate
        switchOrganization(org).then(() => {
          navigate({ to: '/organizations/$orgId', params: { orgId: row.id } });
        });
      } else {
        // Already selected, navigate immediately
        navigate({ to: '/organizations/$orgId', params: { orgId: row.id } });
      }
    }
  };

  const columns: ColumnDef<OrganizationData>[] = [
    {
      accessorKey: 'name',
      header: 'My Organizations',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <div className='font-medium'>{row.getValue('name')}</div>
          {row.original.isSelected && (
            <Badge className='bg-green-100 text-green-800 hover:bg-green-100'>
              <Check className='h-3 w-3 mr-1' />
              Current
            </Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'user_role',
      header: 'Role',
      cell: ({ row }) => {
        const role = row.getValue('user_role') as string;
        return <div>{role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()}</div>;
      },
    },
    {
      accessorKey: 'user_count',
      header: 'Members',
      cell: ({ row }) => {
        const count = row.getValue('user_count') as number;
        return <div className="text-center">{count}</div>;
      },
    },
    {
      accessorKey: 'campaign_count',
      header: 'Campaigns',
      cell: ({ row }) => {
        const count = row.getValue('campaign_count') as number;
        return <div className="text-center">{count}</div>;
      },
    },
    {
      accessorKey: 'plan_type',
      header: 'Plan',
      cell: ({ row }) => {
        const plan = row.getValue('plan_type') as string;
        return <div>{plan.charAt(0).toUpperCase() + plan.slice(1).toLowerCase()}</div>;
      },
    },
    {
      accessorKey: 'google_ads_account',
      header: 'Connection',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <img src={googleAds} alt="Google Ads" className="w-5 h-5 flex-shrink-0" />
          <span>{row.getValue('google_ads_account')}</span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const org = organizations.find(o => o.id?.toString() === row.original.id);
        return (
          <div className='flex justify-end'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant='ghost' 
                  className='h-8 w-8 p-0'
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className='h-4 w-4' />
                  <span className='sr-only'>Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    org && switchOrganization(org);
                  }}
                  disabled={row.original.isSelected || isSwitching}
                  className='cursor-pointer'
                >
                  <CheckCircle className='mr-2 h-4 w-4' />
                  Activate
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    if (org && !row.original.isSelected) {
                      // Switch organization first, then navigate
                      switchOrganization(org).then(() => {
                        navigate({ to: '/organizations/$orgId', params: { orgId: row.original.id } });
                      });
                    } else {
                      // Already selected, navigate immediately without switching
                      navigate({ to: '/organizations/$orgId', params: { orgId: row.original.id } });
                    }
                  }}
                  disabled={isSwitching}
                  className='cursor-pointer'
                >
                  <Eye className='mr-2 h-4 w-4' />
                  Manage
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsGoogleAdsModalOpen(true);
                  }}
                  disabled={!row.original.isSelected || isSwitching}
                  className='cursor-pointer'
                >
                  <Link2 className='mr-2 h-4 w-4' />
                  Switch Google Ads account
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    if (org) {
                      setTargetOrgForTransfer(org);
                      setIsTransferModalOpen(true);
                    }
                  }}
                  disabled={isSwitching}
                  className='cursor-pointer'
                >
                  <ArrowLeftRight className='mr-2 h-4 w-4' />
                  Transfer campaigns to here
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between py-4 h-[72px]">
        <div className="flex items-center gap-3">
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant='outline'
            size='sm'
            onClick={() => {
              console.log('🏢 [SETTINGS] CREATE button clicked - starting organization creation flow');
              navigate({ to: '/welcome' });
            }}
          >
            <Plus className='h-4 w-4 mr-1' />
            New
          </Button>
        </div>
      </div>

      {/* Table */}
      <DataTable
        data={organizationsTableData}
        columns={columns}
        itemsName='organization'
        pageSize={9999}
        onRowClick={handleRowClick}
      />

      <GoogleAdAccountSelectionModal
        isOpen={isGoogleAdsModalOpen}
        onClose={() => setIsGoogleAdsModalOpen(false)}
      />

      {targetOrgForTransfer && (
        <TransferCampaignsModal
          isOpen={isTransferModalOpen}
          onClose={() => {
            setIsTransferModalOpen(false);
            setTargetOrgForTransfer(null);
          }}
          targetOrganization={targetOrgForTransfer}
          activeOrganization={organizationData || null}
          availableOrganizations={organizations}
          onTransfer={handleTransfer}
          isLoading={transferMutation.isPending}
        />
      )}

    </div>
  );
};